import { heartTierColor, rgbColor } from "./drawing";
import { entityOutlineColor, entityOutlineWidth } from "./entity_style";
import type { SnapshotEntity } from "./types";

export function drawPlayer(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  entity: SnapshotEntity,
  flashColor: string | null
): void {
  const ballColor = rgbColor(
    entity.cosmetics?.ball_color ?? entity.cosmetics?.ballColor,
    "#4287f5"
  );
  const fillColor = flashColor ?? ballColor;

  context.save();

  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.fillStyle = fillColor;
  context.fill();
  context.lineWidth = entityOutlineWidth(radius);
  context.strokeStyle = entityOutlineColor(fillColor);
  context.stroke();

  context.beginPath();
  context.arc(x - radius * 0.24, y - radius * 0.26, radius * 0.36, 0, Math.PI * 2);
  context.fillStyle = "rgba(255,255,255,0.22)";
  context.fill();

  context.restore();
}

export function drawZombie(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  entity: SnapshotEntity,
  flashColor: string | null
): void {
  const fillColor = flashColor ?? "#72a95c";

  context.save();

  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.fillStyle = fillColor;
  context.fill();
  context.lineWidth = entityOutlineWidth(radius);
  context.strokeStyle = "#b91c1c";
  context.stroke();

  drawZombieVeins(context, x, y, radius, seedFromEntity(entity));

  context.beginPath();
  context.arc(x - radius * 0.24, y - radius * 0.26, radius * 0.36, 0, Math.PI * 2);
  context.fillStyle = "rgba(255,255,255,0.22)";
  context.fill();

  context.restore();
}

export function drawHeart(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  tier: string | null | undefined
): void {
  const fill = heartTierColor(tier);
  const size = Math.max(6, radius * 1.35);

  context.save();
  context.translate(x, y);

  context.beginPath();
  context.moveTo(0, size * 0.55);
  context.bezierCurveTo(
    -size * 1.05,
    -size * 0.18,
    -size * 0.9,
    -size * 1.05,
    0,
    -size * 0.34
  );
  context.bezierCurveTo(
    size * 0.9,
    size * -1.05,
    size * 1.05,
    size * -0.18,
    0,
    size * 0.55
  );
  context.closePath();
  context.fillStyle = fill;
  context.fill();
  context.strokeStyle = entityOutlineColor(fill);
  context.lineWidth = entityOutlineWidth(size);
  context.lineJoin = "round";
  context.lineCap = "round";
  context.stroke();

  context.restore();
}

function drawZombieVeins(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  seed: number
): void {
  const random = createSeededRandom(seed);
  const branchCount = 3 + Math.floor(random() * 4);
  const sector = (Math.PI * 2) / branchCount;
  const startAngle = random() * Math.PI * 2;
  const jitterLimit = sector * 0.22;

  context.save();
  context.translate(x, y);

  context.beginPath();
  context.arc(0, 0, radius, 0, Math.PI * 2);
  context.clip();

  for (let branchIndex = 0; branchIndex < branchCount; branchIndex += 1) {
    const baseAngle = normalizeAngle(startAngle + branchIndex * sector);
    const veinAngle = normalizeAngle(baseAngle + (random() * 2 - 1) * jitterLimit);
    const points = trimVeinInnerEnd(createVeinPoints(random, veinAngle, radius), radius * 0.22);

    strokeVeinPath(context, points, Math.max(1.15, radius * 0.052), "rgba(127,29,29,0.72)");
    strokeVeinPath(context, points, Math.max(0.75, radius * 0.027), "rgba(239,68,68,0.88)");
  }

  context.restore();
}

function createVeinPoints(
  random: () => number,
  baseAngle: number,
  radius: number
): Array<{ x: number; y: number }> {
  const segmentCount = 3 + Math.floor(random() * 2);
  const points: Array<{ x: number; y: number }> = [];
  let currentAngle = baseAngle;
  let currentRadius = radius * (0.08 + random() * 0.06);

  points.push({
    x: Math.cos(currentAngle) * currentRadius,
    y: Math.sin(currentAngle) * currentRadius
  });

  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex += 1) {
    currentAngle = normalizeAngle(currentAngle + (random() * 2 - 1) * 0.34);
    currentRadius = Math.min(
      radius * (0.76 + random() * 0.08),
      currentRadius + radius * (0.14 + random() * 0.08)
    );

    points.push({
      x: Math.cos(currentAngle) * currentRadius,
      y: Math.sin(currentAngle) * currentRadius
    });
  }

  return points;
}

function trimVeinInnerEnd(
  points: Array<{ x: number; y: number }>,
  trimLength: number
): Array<{ x: number; y: number }> {
  const first = points[0];
  const second = points[1];

  if (!first || !second) {
    return points;
  }

  const dx = second.x - first.x;
  const dy = second.y - first.y;
  const length = Math.hypot(dx, dy);

  if (length <= 0.001) {
    return points;
  }

  const amount = Math.min(trimLength / length, 0.82);

  return [
    {
      x: first.x + dx * amount,
      y: first.y + dy * amount
    },
    ...points.slice(1)
  ];
}

function strokeVeinPath(
  context: CanvasRenderingContext2D,
  points: Array<{ x: number; y: number }>,
  lineWidth: number,
  strokeStyle: string
): void {
  const firstPoint = points[0];

  if (!firstPoint || points.length < 2) {
    return;
  }

  context.beginPath();
  context.moveTo(firstPoint.x, firstPoint.y);

  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];

    if (point) {
      context.lineTo(point.x, point.y);
    }
  }

  context.lineWidth = lineWidth;
  context.strokeStyle = strokeStyle;
  context.lineJoin = "round";
  context.lineCap = "round";
  context.stroke();
}

function seedFromEntity(entity: SnapshotEntity): number {
  return hashString(String(entity.id ?? ""));
}

function hashString(value: string): number {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createSeededRandom(seed: number): () => number {
  let state = seed || 1;

  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;

    return ((state >>> 0) % 10000) / 10000;
  };
}

function normalizeAngle(angle: number): number {
  return angle - Math.PI * 2 * Math.floor(angle / (Math.PI * 2));
}